// Application API Layer
// Endpoints and events are read from YAML domain registry — single source of truth.
//
// Deploy/build calls are delegated to ./applicationDeployApi.
// Logs calls are delegated to ./applicationLogsApi.
// Status/monitoring calls are delegated to ./applicationMetricsApi.

import {
  ApplicationEvents,
  ApplicationEndpoints,
} from "@generated/application";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import {
  eventBus as globalEventBus,
  EventBus,
} from "src/platform/events/EventBus";
import API from "src/platform/http/shared-http";
import debugLogger from "src/platform/utils/debugLogger";

import {
  Application,
  ApplicationListResponse,
  CreateApplicationRequest,
  UpdateApplicationRequest,
  ApplicationEventPayload,
} from "../types/application.types";

import {
  deployApplication,
  deployOnly,
  buildAndDeploy,
  retryRun,
  forceFailBuild,
} from "./applicationDeployApi";
import { getBuildLogs, getServiceLogs } from "./applicationLogsApi";
import type { IApplicationApi } from "./IApplicationApi";

export interface ApplicationListParams {
  projectAlias?: string;
  /** Retained for legacy callers; organization scope comes from the JWT. */
  organizationAlias?: string;
  status?: string;
  type?: string;
  offset?: number;
  limit?: number;
}

// Re-export sub-module helpers so existing `import *` callers keep working.
export {
  deployApplication,
  deployOnly,
  buildAndDeploy,
  retryRun,
  forceFailBuild,
} from "./applicationDeployApi";
export { getBuildLogs, getServiceLogs } from "./applicationLogsApi";

export class ApplicationApi implements IApplicationApi {
  private eventBus: EventBus;

  constructor(eventBus: EventBus) {
    this.eventBus = eventBus;
  }

  async getApplication(alias: string): Promise<Application> {
    try {
      debugLogger.info("application-api", `Fetching application: ${alias}`);

      const api = new API(
        apiBaseUrls.BACKEND,
        ApplicationEndpoints.GET_APPLICATION.replace(
          ":id",
          encodeURIComponent(alias),
        ),
      );
      const response = await api.get<Application>();

      this.eventBus.emitGlobal(ApplicationEvents.APPLICATION_LOADED, {
        application: response,
      } as ApplicationEventPayload);

      debugLogger.info("application-api", `Application loaded: ${alias}`);
      return response;
    } catch (error) {
      const errorMessage = `Failed to fetch application: ${alias}`;
      debugLogger.error("application-api", errorMessage, error);

      this.eventBus.emitGlobal(ApplicationEvents.APPLICATION_ERROR, {
        error: errorMessage,
      } as ApplicationEventPayload);

      throw error;
    }
  }

  async getApplications(
    params?: ApplicationListParams,
  ): Promise<ApplicationListResponse> {
    try {
      debugLogger.info("application-api", "Fetching applications");

      const queryParams = new URLSearchParams();
      if (params?.projectAlias)
        queryParams.append("projectAlias", params.projectAlias);
      if (params?.status) queryParams.append("status", params.status);
      if (params?.type) queryParams.append("type", params.type);
      if (params?.limit) queryParams.append("limit", params.limit.toString());
      if (params?.offset)
        queryParams.append("offset", params.offset.toString());

      const basePath = ApplicationEndpoints.LIST_APPLICATIONS;
      const endpoint = queryParams.toString()
        ? `${basePath}?${queryParams.toString()}`
        : basePath;
      const api = new API(apiBaseUrls.BACKEND, endpoint);
      const response = await api.get<ApplicationListResponse>();

      this.eventBus.emitGlobal(ApplicationEvents.APPLICATIONS_LOADED, {
        applications: response.applications,
      } as ApplicationEventPayload);

      debugLogger.info(
        "application-api",
        `Applications loaded: ${response.applications.length} found`,
      );
      return response;
    } catch (error) {
      const errorMessage = "Failed to fetch applications";
      debugLogger.error("application-api", errorMessage, error);

      this.eventBus.emitGlobal(ApplicationEvents.APPLICATION_ERROR, {
        error: errorMessage,
      } as ApplicationEventPayload);

      throw error;
    }
  }

  async createApplication(
    request: CreateApplicationRequest,
  ): Promise<Application> {
    try {
      debugLogger.info("application-api", "Creating application", request);

      const api = new API(
        apiBaseUrls.BACKEND,
        ApplicationEndpoints.CREATE_APPLICATION,
      );
      const response = await api.post<Application>(request);

      this.eventBus.emitGlobal(ApplicationEvents.APPLICATION_CREATED, {
        application: response,
      } as ApplicationEventPayload);

      debugLogger.info("application-api", "Application created successfully");
      return response;
    } catch (error) {
      const errorMessage = "Failed to create application";
      debugLogger.error("application-api", errorMessage, error);

      this.eventBus.emitGlobal(ApplicationEvents.APPLICATION_ERROR, {
        error: errorMessage,
      } as ApplicationEventPayload);

      throw error;
    }
  }

  async updateApplication(
    alias: string,
    request: UpdateApplicationRequest,
  ): Promise<Application> {
    try {
      debugLogger.info(
        "application-api",
        `Updating application: ${alias}`,
        request,
      );

      const api = new API(
        apiBaseUrls.BACKEND,
        ApplicationEndpoints.UPDATE_APPLICATION.replace(
          ":id",
          encodeURIComponent(alias),
        ),
      );
      const response = await api.patch<Application>(request);

      this.eventBus.emitGlobal(ApplicationEvents.APPLICATION_UPDATED, {
        application: response,
      } as ApplicationEventPayload);

      debugLogger.info("application-api", `Application updated: ${alias}`);
      return response;
    } catch (error) {
      const errorMessage = `Failed to update application: ${alias}`;
      debugLogger.error("application-api", errorMessage, error);

      this.eventBus.emitGlobal(ApplicationEvents.APPLICATION_ERROR, {
        error: errorMessage,
      } as ApplicationEventPayload);

      throw error;
    }
  }

  async deleteApplication(alias: string): Promise<void> {
    try {
      debugLogger.info("application-api", `Deleting application: ${alias}`);

      const api = new API(
        apiBaseUrls.BACKEND,
        ApplicationEndpoints.DELETE_APPLICATION.replace(
          ":id",
          encodeURIComponent(alias),
        ),
      );
      await api.delete();

      this.eventBus.emitGlobal(ApplicationEvents.APPLICATION_DELETED, {
        applicationAlias: alias,
      } as ApplicationEventPayload);

      debugLogger.info("application-api", `Application deleted: ${alias}`);
    } catch (error) {
      const errorMessage = `Failed to delete application: ${alias}`;
      debugLogger.error("application-api", errorMessage, error);

      this.eventBus.emitGlobal(ApplicationEvents.APPLICATION_ERROR, {
        error: errorMessage,
      } as ApplicationEventPayload);

      throw error;
    }
  }

  async deployApplication(
    alias: string,
    targetEnvironmentSlug: string,
    images?: Record<string, string>,
  ): Promise<{ id: string; status: string }> {
    return deployApplication(
      this.eventBus,
      alias,
      targetEnvironmentSlug,
      images,
    );
  }

  async deployOnly(
    alias: string,
    images?: Record<string, string>,
    targetEnvironmentSlug?: string,
  ): Promise<{
    jobId: string;
    status: string;
    systemAlias: string;
    images?: Record<string, string>;
  }> {
    return deployOnly(this.eventBus, alias, images, targetEnvironmentSlug);
  }

  async buildAndDeploy(
    alias: string,
    targetEnvironmentSlug?: string,
  ): Promise<{
    jobId: string;
    status: string;
    systemAlias: string;
    sha: string;
  }> {
    return buildAndDeploy(this.eventBus, alias, targetEnvironmentSlug);
  }

  async retryRun(
    alias: string,
    sagaId: string,
    intent: "release" | "deploy" = "release",
  ): Promise<{
    jobId: string;
    status: string;
    systemAlias: string;
    sha: string;
  }> {
    return retryRun(this.eventBus, alias, sagaId, intent);
  }

  async forceFailBuild(alias: string): Promise<{ success: boolean }> {
    return forceFailBuild(this.eventBus, alias);
  }

  async getBuildLogs(
    alias: string,
    serviceName?: string,
    options?: { sagaId?: string; phase?: string },
  ): Promise<import("./IApplicationApi").BuildLogsResult> {
    return getBuildLogs(alias, serviceName, options);
  }

  async getServiceLogs(
    alias: string,
    serviceName: string,
    options?: { tailLines?: number; sinceSeconds?: number },
  ): Promise<import("./IApplicationApi").ServiceLogsResult> {
    return getServiceLogs(alias, serviceName, options);
  }
}

// Singleton instance for direct use by hooks
const applicationApi = new ApplicationApi(globalEventBus);

export default applicationApi;
